import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NivaLogo from '../components/NivaLogo';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { generateShiftHandoverDraft } from '../services/openAIService';
import {
  buildIncompleteTaskDetails,
  compilePatientShiftData,
  createEmptyHandoverDraft,
  groupSessionNotesByPatient,
  mergeCarePlanPatientsIntoGroups,
  normalizeHandoverDraft,
} from '../utils/shiftHandoverCompiler';
import { ROUTES } from '../constants/routes';

export default function ShiftHandoverGeneratingScreen() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    sessionNotes,
    getFlowSheetData,
    getPatientTasks,
    getPatientCarePlans,
    carePlansByPatient,
    admittedPatients,
    setShiftHandoverDrafts,
  } = useSession();
  const [statusText, setStatusText] = useState(t('compilingShiftHandover'));
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    let cancelled = false;

    async function run() {
      const noteGroups = groupSessionNotesByPatient(sessionNotes);
      const patientGroups = mergeCarePlanPatientsIntoGroups(
        noteGroups,
        carePlansByPatient,
        admittedPatients
      );

      if (patientGroups.length === 0) {
        setShiftHandoverDrafts([]);
        if (!cancelled) navigate(ROUTES.SHIFT_HANDOVER, { replace: true });
        return;
      }

      const drafts = [];

      for (let index = 0; index < patientGroups.length; index += 1) {
        const group = patientGroups[index];
        if (cancelled) return;

        setStatusText(
          `${t('compilingForPatient')} ${group.patientName} (${index + 1}/${patientGroups.length})`
        );

        const flowSheetData = group.patientId ? getFlowSheetData(group.patientId) : null;
        const incompleteTaskDetails = buildIncompleteTaskDetails(
          group.patientId ? getPatientTasks(group.patientId) : []
        );
        const carePlans = group.patientId
          ? getPatientCarePlans(group.patientId)
          : [];
        const compiled = compilePatientShiftData(
          group,
          flowSheetData,
          sessionNotes,
          incompleteTaskDetails,
          carePlans
        );

        try {
          const generated = await generateShiftHandoverDraft(compiled);
          drafts.push(normalizeHandoverDraft(generated, group, incompleteTaskDetails));
        } catch (error) {
          console.error('Shift handover generation failed:', error);
          const fallback = createEmptyHandoverDraft(group);
          fallback.status = 'error';
          fallback.error = error.message || 'Generation failed';
          fallback.outstanding_task_details = incompleteTaskDetails;
          fallback.outstanding_tasks = incompleteTaskDetails.map((task) => task.title);
          drafts.push(fallback);
        }
      }

      if (cancelled) return;
      setShiftHandoverDrafts(drafts);
      navigate(ROUTES.SHIFT_HANDOVER, { replace: true });
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [
    admittedPatients,
    carePlansByPatient,
    getFlowSheetData,
    getPatientCarePlans,
    getPatientTasks,
    navigate,
    sessionNotes,
    setShiftHandoverDrafts,
    t,
  ]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-page px-6">
      <NivaLogo className="mb-10" />
      <div className="spinner mb-4 h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary" />
      <p className="text-center text-sm font-medium text-gray-600">{statusText}</p>
    </div>
  );
}
